import * as React from 'react';
import classnames from 'classnames';
import {useNavigate} from "react-router";

import {HeadlineLevel, SITE} from 'features/ui/enums';
import {EpiImage} from "features/search/types";
import {IContentBlock} from 'features/blocks/types';
import {ImageSizes} from '@organisms/Shell/image-size-enum';
import {getCurrentSite} from 'features/services/utils/env-helper';
import {useDeviceStore, selectIsMobile, selectIsTablet} from 'features/ui/store/useDeviceStore';

import 'components/blocks/Text/CampaignBlock/style.scss';

import {P} from '@atoms/Text/P';
import {Headline} from '@atoms/Text/Headline';
import {ReadMore} from '@atoms/LinksAndButtons/ReadMore';
import {MainGrid} from '@atoms/Layout/MainGrid';
import {AsyncImage} from '@atoms/Media/AsyncImage';
import {ButtonLink} from '@atoms/LinksAndButtons/ButtonLink';
import {VideoBlock} from "components/blocks/ImageAndVideo/VideoBlock";
import { ButtonColor, ButtonSize, ButtonType } from '@atoms/LinksAndButtons/Button';

export enum CampaignBlockButtonType {
    GHOST = 'ghost',
    PRIMARY = 'primary',
    READ_MORE = 'readmore'
}

export interface CampaignBlockType {
    title: string;
    text: string;
    youtubeLink?: string;
    youtubeDescription?: string;
    youtubeScriptText?: string;
    campaignLinkUrl: string;
    buttonText: string;
    buttonType?: CampaignBlockButtonType;
    image?: EpiImage;
    theme: string;
    internalLink: boolean;
}
type Props = IContentBlock<CampaignBlockType>;

export const CampaignBlock: React.FunctionComponent<Props> = ({data, id, }) => {
    const isMobile = useDeviceStore(selectIsMobile);
    const isTablet = useDeviceStore(selectIsTablet);
    const navigate = useNavigate();

    const onClick = () => {
        if(data.campaignLinkUrl.indexOf('mailto:') > -1 || data.campaignLinkUrl.indexOf('tel:') > -1) {
            window.open(data.campaignLinkUrl);
            return;
        }

        if(!data.internalLink) {
            window.open(data.campaignLinkUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        navigate(data.campaignLinkUrl)
    };

    if(!data || (data.title === null && data.text === null)) return null;

    const currentSite = getCurrentSite();

    const campaignClasses = classnames('campaign-block', {
        'campaign-block--no-image': !data.image || !data.image.url,
        'campaign-block--dark dark-bg': data.theme === 'Dark',
        'campaign-block--light': data.theme === 'Light',
        'campaign-block--gray': data.theme === 'Gray',
        'campaign-block--green': data.theme === 'Sustainability green',
    });

    return (
        <MainGrid fullwidth className={campaignClasses} id={id}>
            <div className="campaign-block__text">
                <Headline 
                    level={HeadlineLevel.H2} 
                >
                    {data.title}
                </Headline>
                <P className="line-breaks">{data.text}</P>

                {data.buttonText && data.campaignLinkUrl &&
                    <>
                        {data.buttonType === CampaignBlockButtonType.READ_MORE ?
                            <ReadMore
                                text={data.buttonText}
                                link={data.campaignLinkUrl}
                                bold
                            /> :
                            <ButtonLink 
                                onClick={onClick}
                                href={data.campaignLinkUrl}
                                preventDefault
                                forceExternal={!data.internalLink} 
                                buttonColor={data.buttonType === CampaignBlockButtonType.PRIMARY
                                    ? ButtonColor.CTA
                                    : data.theme === 'Dark'
                                        ? ButtonColor.LIGHT
                                        : ButtonColor.DEFAULT} 
                                buttonSize={ButtonSize.DEFAULT} 
                                buttonType={((!data.buttonType || data.buttonType === CampaignBlockButtonType.GHOST) && (!(currentSite === SITE.BORATT && !data.image && data.theme !== 'Dark'))) 
                                    ? ButtonType.GHOST
                                    : ButtonType.DEFAULT} 
                            >
                                {data.buttonText}
                            </ButtonLink>
                        }
                    </>
                }
            </div>

           {data.image &&
                <div className="campaign-block__image">
                     {data.youtubeLink 
                        ? <VideoBlock
                            data={{
                                videoUrl: data.youtubeLink,
                                description: data.youtubeDescription,
                                hideDescriptionOnPage: true,
                                scriptText: data.youtubeScriptText,
                                title: '',
                                image: data.image,
                            }} 
                            component={''} 
                            renderStyle={''} 
                        />
                        : <AsyncImage
                            image={data.image}
                            quality={ImageSizes.SIZE_900}
                            fullHeight={!isMobile || isTablet}
                            fullWidth={isMobile && !isTablet}
                            //alt={data.title}
                        />
                    }
                </div>
            }
        </MainGrid>
    )
};

